// KidPantryManager — adult-facing sheet listing every pantry item in
// the family (anything you've scanned or added to your kitchen) with
// a per-row toggle that flags the SPECIFIC product as requestable by
// the kids' Foods tab. Migration 0244 added kid_visible / kid_label /
// kid_kind columns on pantry_items; this sheet is the parent's
// touch point for them. Mirrors the shape of KidMealsManager but
// targets pantry_items instead of kid_recipes.
//
// Why row-level: a kid asking for "the Capri Sun in the fridge" is
// fundamentally different from asking for "juice". The pantry-row
// path preserves brand, barcode, and the specific product so the
// inbox can show "Lila asked for Slim Jim Eggwich" with the actual
// item, not a generic "egg sandwich".
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**) is
// the explicit opt-in to emojis.

import { useEffect, useMemo, useRef, useState } from "react";
import { COLOR, FONT, RADIUS } from "../../lib/tokens";
import { supabase } from "../../lib/supabase";

const KIND_OPTIONS = [
  { key: "meal",    label: "Meal",  emoji: "🍽️" },
  { key: "snack",   label: "Snack", emoji: "🥨" },
  { key: "dessert", label: "Treat", emoji: "🍰" },
];

const SELECT_COLS = [
  "id", "user_id", "name", "brand", "emoji", "canonical_id",
  "barcode_upc", "kid_visible", "kid_label", "kid_kind",
  "updated_at",
].join(", ");

export function KidPantryManager({ userId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showOnlyMarked, setShowOnlyMarked] = useState(false);

  // Pull every pantry_items row in the family. RLS scopes it for us.
  // Realtime sub keeps the list in sync if a row gets renamed,
  // re-canonical'd, or another adult flips kid_visible elsewhere.
  const reload = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("pantry_items")
      .select(SELECT_COLS)
      .order("kid_visible", { ascending: false })
      .order("updated_at",  { ascending: false })
      .limit(500);
    if (error) console.warn("[kid-pantry-mgr] load:", error.message);
    setRows(data || []);
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const ch = supabase
      .channel(`rt:kid_pantry_mgr:${userId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "pantry_items" },
        () => reload())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const filtered = useMemo(() => {
    let xs = rows;
    if (showOnlyMarked) xs = xs.filter((r) => r.kid_visible);
    const q = query.trim().toLowerCase();
    if (q) {
      xs = xs.filter((r) => {
        const hay = `${r.brand || ""} ${r.name || ""} ${r.kid_label || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return xs;
  }, [rows, query, showOnlyMarked]);

  const markedCount = useMemo(() => rows.filter((r) => r.kid_visible).length, [rows]);

  // Patch helper — updates one row by id. Optimistic local update,
  // realtime sub reconciles. Family RLS lets any adult write to
  // family-owned rows.
  const patchRow = async (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    const { error } = await supabase
      .from("pantry_items")
      .update(patch)
      .eq("id", id);
    if (error) {
      console.warn("[kid-pantry-mgr] patch:", error.message);
      reload();
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(30,30,30,0.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 220,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          maxHeight: "85vh",
          background: "#FAF6EE",
          borderRadius: RADIUS.sheet,
          padding: "20px 0 calc(16px + env(safe-area-inset-bottom))",
          boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ padding: "0 20px 12px", flexShrink: 0 }}>
          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 22,
              color: COLOR.ink,
              marginBottom: 4,
            }}
          >
            Kid Items
          </div>
          <div
            style={{
              fontFamily: FONT.forma,
              fontSize: 13,
              color: COLOR.muted,
              marginBottom: 12,
              lineHeight: 1.5,
            }}
          >
            Tap any pantry item to let the kids request it.
            Give it a kid-friendly nickname and mark it as a Meal,
            Snack, or Treat — it'll show up in their Foods → Recipes
            or Treats tab.
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search items…"
              style={{
                flex: 1,
                padding: "12px 14px",
                fontFamily: "inherit",
                fontSize: 16,
                background: "#fff",
                border: `1px solid ${COLOR.border}`,
                borderRadius: 10,
                color: COLOR.ink,
                boxSizing: "border-box",
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => setShowOnlyMarked((v) => !v)}
            className="mcm-focusable"
            style={{
              alignSelf: "flex-start",
              padding: "6px 10px",
              background: showOnlyMarked ? COLOR.leafDeep : "transparent",
              color: showOnlyMarked ? COLOR.leaf : COLOR.muted,
              border: `1px solid ${showOnlyMarked ? COLOR.leafDim : COLOR.border}`,
              borderRadius: 8,
              fontFamily: FONT.mono,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              cursor: "pointer",
              touchAction: "manipulation",
            }}
          >
            {showOnlyMarked ? `✓ ONLY KID ITEMS (${markedCount})` : `SHOW ONLY KID ITEMS (${markedCount})`}
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            padding: "4px 20px 20px",
          }}
        >
          {loading && filtered.length === 0 ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: COLOR.muted, fontFamily: FONT.forma, fontSize: 13 }}>
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: COLOR.muted, fontFamily: FONT.forma, fontSize: 13 }}>
              No items match.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {filtered.map((row) => (
                <PantryItemRow
                  key={row.id}
                  row={row}
                  onToggle={() => patchRow(row.id, {
                    kid_visible: !row.kid_visible,
                    // Default new toggles to 'snack' (most pantry
                    // items are snacks; meal-shaped rows are rarer).
                    kid_kind: row.kid_kind || "snack",
                  })}
                  onMetaChange={(patch) => patchRow(row.id, patch)}
                />
              ))}
            </div>
          )}
        </div>

        <div style={{ padding: "12px 20px 0", flexShrink: 0 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "100%",
              padding: 14,
              background: COLOR.leaf,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.08em",
              cursor: "pointer",
            }}
          >
            DONE
          </button>
        </div>
      </div>
    </div>
  );
}

function PantryItemRow({ row, onToggle, onMetaChange }) {
  const marked = !!row.kid_visible;
  const [localLabel, setLocalLabel] = useState(row.kid_label || "");
  const lastWroteRef = useRef(row.kid_label || "");
  useEffect(() => {
    if (typeof row.kid_label !== "string" && row.kid_label !== null) return;
    const v = row.kid_label || "";
    if (v === lastWroteRef.current) return;
    setLocalLabel(v);
  }, [row.kid_label]);

  useEffect(() => {
    if (!marked) return undefined;
    const v = (row.kid_label || "");
    if (localLabel === v) return undefined;
    const id = setTimeout(() => {
      lastWroteRef.current = localLabel;
      onMetaChange?.({ kid_label: localLabel.trim() || null });
    }, 380);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localLabel, marked]);

  const kind = row.kid_kind || "snack";
  const display = row.brand ? `${row.brand} ${row.name || ""}`.trim() : (row.name || "Item");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: marked ? 8 : 0,
        padding: marked ? "12px 14px" : "0",
        background: marked ? COLOR.leafDeep : "transparent",
        border: marked ? `1px solid ${COLOR.leafDim}` : "none",
        borderRadius: 10,
        transition: "background 120ms ease, border-color 120ms ease",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        style={{
          all: "unset",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: marked ? 0 : "12px 14px",
          background: marked ? "transparent" : "#fff",
          border: marked ? "none" : `1px solid ${COLOR.border}`,
          borderRadius: marked ? 0 : 10,
          touchAction: "manipulation",
        }}
      >
        <span style={{ fontSize: 26, lineHeight: 1 }}>{row.emoji || "🥫"}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: FONT.forma,
              fontSize: 15,
              fontWeight: 600,
              color: COLOR.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {display}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, letterSpacing: "0.06em" }}>
            {row.barcode_upc ? `UPC ${row.barcode_upc}` : "MANUAL"}
          </div>
        </div>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: marked ? COLOR.leaf : "transparent",
            border: `2px solid ${marked ? COLOR.leaf : COLOR.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 16,
            fontWeight: 700,
          }}
        >
          {marked ? "✓" : ""}
        </div>
      </button>

      {marked && (
        <>
          <input
            type="text"
            value={localLabel}
            onChange={(e) => setLocalLabel(e.target.value)}
            placeholder={`What kids call it (default: ${display})`}
            maxLength={48}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontFamily: "inherit",
              fontSize: 14,
              background: "#fff",
              border: `1px solid ${COLOR.border}`,
              borderRadius: 8,
              color: COLOR.ink,
              boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", gap: 6 }}>
            {KIND_OPTIONS.map((k) => {
              const active = kind === k.key;
              return (
                <button
                  key={k.key}
                  type="button"
                  onClick={() => onMetaChange?.({ kid_kind: k.key })}
                  className="mcm-focusable"
                  style={{
                    flex: 1,
                    padding: "8px 4px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    background: active ? COLOR.leaf : "#fff",
                    border: `1.5px solid ${active ? COLOR.leaf : COLOR.border}`,
                    borderRadius: 8,
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  <span style={{ fontSize: 18, lineHeight: 1 }}>{k.emoji}</span>
                  <span
                    style={{
                      fontFamily: FONT.mono,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      color: active ? "#fff" : COLOR.ink,
                    }}
                  >
                    {k.label.toUpperCase()}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default KidPantryManager;
